import {
  EditOutlined,
  ExperimentOutlined,
  FileSearchOutlined,
  HistoryOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  RobotOutlined,
  SearchOutlined,
  StopOutlined,
  UndoOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Button, Modal, Space, Tag, Tooltip } from 'antd'
import classNames from 'classnames'
import dayjs, { type Dayjs } from 'dayjs'
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { toast } from 'sonner'

import AtomButton from '@/components/Atoms/AtomButton/AtomButton'
import AtomDateRangePicker from '@/components/Atoms/AtomDateRangePicker/AtomDateRangePicker'
import { useAtomModal } from '@/components/Atoms/AtomModal/useAtomModal'
import AtomSelect from '@/components/Atoms/AtomSelect/AtomSelect'
import AtomSwitch from '@/components/Atoms/AtomSwitch/AtomSwitch'
import AtomTable from '@/components/Atoms/AtomTable/AtomTable'
import AtomVirtualizedTable from '@/components/Atoms/AtomVirtualizedTable/AtomVirtualizedTable'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import Loader from '@/components/Atoms/Loader/Loader'
import SupplierCreationModal from '@/components/EntityCreationModals/SupplierCreationModal/SupplierCreationModal'
import { cronOptions, legacyCronMap } from '@/constants'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import { BrandInterface } from '@/types'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { fetchBrands } from '@/utils/api/fetch/fetchEntities'
import { toBackendTimestamp } from '@/utils/api/toBackendTimestamp'
import { getColumnWidths } from '@/utils/table/getColumnWidths'

import { stockImportsApi } from '../api/stockImportsApi'
import ConfigModal from '../ConfigModal/ConfigModal'
import { toExcelRowNumber } from '../lib/stockImportsFormat'
import { handleStockImportsFormError } from '../lib/stockImportsFormError'
import { hasActiveLogFilters } from '../lib/stockImportsLogFilters'
import {
  formatMs,
  getLogSourceInfo,
  LOG_COUNTERS,
  runStatusMap,
  runTypeLabels,
  sourceTypeLabels,
} from '../lib/stockImportsLogLabels'
import {
  formatMissingSkuReason,
  getArchiveConfigWarning,
  RESET_CHECKS_WARNING,
  STOCK_IMPORT_LIVE_CONFIRMATION,
  STOCK_IMPORT_PROGRESS_MESSAGES,
} from '../lib/stockImportsMessages'
import {
  acquireStockImportRun,
  isStockImportLiveRunAllowed,
  isStockImportTrialRunAllowed,
} from '../lib/stockImportsRunValidation'
import {
  appendUniqueLogs,
  findNewestRunStartedAt,
  findSettledError,
  invalidateLogsAndMetricsSnapshot,
  pickSettledValue,
  resolveLogsUpperBound,
} from '../lib/stockImportsSnapshots'
import {
  ALL_SUPPLIERS_SNAPSHOT_KEY,
  buildSupplierOptions,
  isAllSuppliersSelected,
  resolveRequestSupplierId,
  SELF_SUPPLIER_SNAPSHOT_KEY,
} from '../lib/stockImportsSupplierScope'
import MissingSkuModal from '../MissingSkuModal/MissingSkuModal'
import PositionCheckModal from '../PositionCheckModal/PositionCheckModal'
import { labelWithTooltip } from '../RequiredLabel/labelWithTooltip'
import StockImportLogDetailsModal from '../StockImportLogDetailsModal/StockImportLogDetailsModal'
import StockImportsVirtualTable, {
  StockImportsTableColumn,
} from '../StockImportsVirtualTable/StockImportsVirtualTable'
import tableCn from '../StockImportsVirtualTable/StockImportsVirtualTable.module.scss'
import {
  ImportConfigStatus,
  ImportRunStatus,
  ImportRunType,
  ProductSupplierDto,
  StockImportConfigModel,
  StockImportConfigShortModel,
  StockImportExecutionResponse,
  StockImportLogModel,
  StockImportMetricsModel,
  StockImportRunMode,
  StoreModel,
} from '../types/stockImports.types'
import JournalConfigFilter from './JournalConfigFilter/JournalConfigFilter'
import MissingSkuReportMark from './MissingSkuReportMark/MissingSkuReportMark'
import cn from './StockImportsTab.module.scss'

// The run endpoints answer with an execution-result body ({ success, message, loadedCount,
// failedCount, failedDetails }) even on a non-2xx status when every position fails to match.
// Axios rejects those, so pull the body back out and render it as a normal result instead of
// letting handleApiError swallow failedDetails and show only the bare message.
type ExtractedExecutionResult = Omit<
  StockImportExecutionResponse,
  'updatedConfig'
> & {
  updatedConfig?: StockImportConfigModel
}

const extractExecutionResponse = (
  error: unknown
): ExtractedExecutionResult | null => {
  const data = (error as { response?: { data?: unknown } })?.response?.data
  if (!data || typeof data !== 'object') return null
  const body = data as Partial<StockImportExecutionResponse>
  if (typeof body.message !== 'string') return null
  if (
    !Array.isArray(body.failedDetails) &&
    typeof body.failedCount !== 'number' &&
    typeof body.loadedCount !== 'number'
  ) {
    return null
  }
  return {
    success: body.success ?? false,
    message: body.message,
    loadedCount: body.loadedCount,
    failedCount: body.failedCount,
    failedDetails: body.failedDetails,
    logId: body.logId,
    updatedConfig: body.updatedConfig,
  }
}

const statusMap: Record<ImportConfigStatus, { label: string; color: string }> =
  {
    DRAFT: { label: 'Черновик', color: 'default' },
    ACTIVE: { label: 'Активна', color: 'success' },
    ARCHIVED: { label: 'Архив', color: 'warning' },
    ERROR_SUSPENDED: { label: 'Остановлена из-за ошибок', color: 'error' },
  }

interface StatusTooltipItem {
  label: string
  description: string
}

const CONFIG_STATUS_TOOLTIP_ITEMS: StatusTooltipItem[] = [
  {
    label: 'Черновик',
    description:
      'настройка создана или изменена, фоновый запуск отключен. Требуется ручной боевой запуск.',
  },
  {
    label: 'Активна',
    description:
      'настройка проверена и работает в фоновом режиме по расписанию.',
  },
  {
    label: 'Архив',
    description: 'настройка отключена вручную, фоновые запуски остановлены.',
  },
  {
    label: 'Остановлена из-за ошибок',
    description:
      'фоновый импорт завершился критической ошибкой 3 раза подряд. Система приостановила запуски до ручной проверки.',
  },
]

const RUN_STATUS_TOOLTIP_ITEMS: StatusTooltipItem[] = [
  {
    label: 'Успешно',
    description: 'остатки загружены, все строки файла распознаны.',
  },
  {
    label: 'Предупреждение',
    description:
      'остатки загружены, но часть строк не загрузилась (колонка «Не загружено») или есть замечание (колонка «Пояснение»).',
  },
  {
    label: 'Без изменений',
    description:
      'файл не изменился с прошлой загрузки или нового письма нет, остатки прежние.',
  },
  {
    label: 'Ошибка',
    description:
      'загрузка остановлена, остатки склада прежние: файл не получен или не прочитан, сработала защита от сломанного файла или произошёл сбой. Причина — в колонке «Пояснение».',
  },
]

const StatusTooltipContent = ({ items }: { items: StatusTooltipItem[] }) => (
  <ul className={cn.statusTooltipList}>
    {items.map((item) => (
      <li key={item.label}>
        <strong>{item.label}</strong> — {item.description}
      </li>
    ))}
  </ul>
)

const columnHeaderWithTooltip = (
  label: string,
  tooltipItems: StatusTooltipItem[]
) => (
  <span className={cn.labelWithTooltip}>
    {label}
    <Tooltip
      title={<StatusTooltipContent items={tooltipItems} />}
      placement="top"
    >
      <QuestionCircleOutlined className={cn.labelHelpIcon} />
    </Tooltip>
  </span>
)

const LOGS_PAGE_SIZE = 20
const STOCK_IMPORT_SECTION_TABS = [
  { id: 1, name: 'Настройки загрузки' },
  { id: 2, name: 'Журнал загрузок' },
] as const

type RunKindFilter = 'LIVE' | 'TRIAL'

const RUN_KIND_OPTIONS: Array<{ value: RunKindFilter; label: string }> = [
  { value: 'LIVE', label: 'Боевой' },
  { value: 'TRIAL', label: 'Тестовый' },
]

const RUN_TYPE_OPTIONS = (Object.keys(runTypeLabels) as ImportRunType[]).map(
  (value) => ({ value, label: runTypeLabels[value] })
)

const RUN_STATUS_OPTIONS = Object.entries(runStatusMap).map(
  ([value, status]) => ({ value, label: status.label })
)

const isRunStatus = (value: string): value is ImportRunStatus =>
  value in runStatusMap

const isRunType = (value: string): value is ImportRunType =>
  value in runTypeLabels

const lastRunTypeHints: Record<
  ImportRunType,
  { title: string; icon: ReactNode }
> = {
  MANUAL: { title: 'Ручной запуск', icon: <UserOutlined /> },
  AUTO: { title: 'Автозапуск', icon: <RobotOutlined /> },
}

interface LogFilterState {
  configId?: string
  runStatus?: ImportRunStatus
  runKind?: RunKindFilter
  runType?: ImportRunType
  dateFrom?: Dayjs | null
  dateTo?: Dayjs | null
}

const toIsTrialParam = (runKind?: RunKindFilter) =>
  runKind === undefined ? undefined : String(runKind === 'TRIAL')

interface LogsSnapshot {
  logs: StockImportLogModel[]
  logsPage: number
  logsHasMore: boolean
  newestRunStartedAt?: string
}

interface SupplierDetailsSnapshot {
  stores: StoreModel[]
  configs: StockImportConfigShortModel[]
  metricsByFilter: Record<string, StockImportMetricsModel | null>
  logsByFilter: Record<string, LogsSnapshot>
}

const formatRunPeriodCell = (startedAt?: string, endedAt?: string) => {
  const startedDate = startedAt ? dayjs(startedAt) : null
  const endedDate = endedAt ? dayjs(endedAt) : null
  const hasStart = !!startedDate?.isValid()
  const hasEnd = !!endedDate?.isValid()

  if (!hasStart && !hasEnd) return '—'

  const daySource = hasStart ? startedDate! : endedDate!
  const sameDay = hasStart && hasEnd && startedDate!.isSame(endedDate!, 'day')
  const startedTime = hasStart ? startedDate!.format('HH:mm:ss') : '—'
  const endedTime = !hasEnd
    ? '—'
    : sameDay || !hasStart
      ? endedDate!.format('HH:mm:ss')
      : endedDate!.format('DD.MM HH:mm:ss')

  return (
    <div className={cn.logPeriodCell}>
      <span className={cn.logPeriodDate}>{daySource.format('DD.MM.YYYY')}</span>
      <span className={cn.logPeriodTime}>
        {startedTime} → {endedTime}
      </span>
    </div>
  )
}

const formatLastRunCell = (
  lastRunAt?: string,
  lastRunStatus?: ImportRunStatus,
  lastRunType?: ImportRunType | null
) => {
  if (!lastRunAt && !lastRunStatus)
    return <div className={cn.lastRunEmpty}>—</div>

  const runDate = lastRunAt ? dayjs(lastRunAt) : null
  const dateLabel = runDate?.isValid()
    ? runDate.format('DD.MM.YYYY HH:mm:ss')
    : '—'
  const statusInfo = lastRunStatus ? runStatusMap[lastRunStatus] : null
  const runTypeHint = lastRunType ? lastRunTypeHints[lastRunType] : null

  return (
    <div className={cn.logPeriodCell}>
      <span className={cn.logPeriodDate}>{dateLabel}</span>
      <span className={cn.lastRunStatusLine}>
        {statusInfo ? (
          <Tag className={cn.tableStatusTagCompact} color={statusInfo.color}>
            {statusInfo.label}
          </Tag>
        ) : (
          <span className={cn.logPeriodTime}>—</span>
        )}
        {runTypeHint && (
          <Tooltip title={runTypeHint.title}>
            <span className={cn.lastRunTypeIcon} aria-label={runTypeHint.title}>
              {runTypeHint.icon}
            </span>
          </Tooltip>
        )}
      </span>
    </div>
  )
}

const truncateFileHash = (hash?: string) => {
  if (!hash) return null
  const TRUNCATED_LENGTH = 4 + '...'.length + 4
  if (hash.length <= TRUNCATED_LENGTH) return hash
  return `${hash.slice(0, 4)}...${hash.slice(-4)}`
}

const formatCronExpression = (value?: string | null) => {
  if (!value) return '—'

  const normalizedValue = legacyCronMap[value] ?? value
  return (
    cronOptions.find((option) => option.value === normalizedValue)?.label ??
    normalizedValue
  )
}

const buildAnalyticsParams = (
  supplierId: string | null,
  filters: LogFilterState,
  page?: number,
  newestRunStartedAt?: string
) => ({
  productSupplierId: supplierId,
  configId: filters.configId,
  runStatus: filters.runStatus,
  isTrial: toIsTrialParam(
    page === undefined ? (filters.runKind ?? 'LIVE') : filters.runKind
  ),
  runType: filters.runType,
  runStartedAtFrom: toBackendTimestamp(filters.dateFrom?.startOf('day')),
  runStartedAtTo: toBackendTimestamp(
    resolveLogsUpperBound(
      filters.dateTo?.endOf('day'),
      newestRunStartedAt,
      dayjs()
    )
  ),
  ...(page === undefined
    ? {}
    : {
        page,
        pageSize: LOGS_PAGE_SIZE,
        runStartedAtOrderBy: 'DESC',
      }),
})

const buildLogFiltersKey = (filters: LogFilterState) =>
  JSON.stringify({
    configId: filters.configId ?? null,
    runStatus: filters.runStatus ?? null,
    runKind: filters.runKind ?? null,
    runType: filters.runType ?? null,
    dateFrom: toBackendTimestamp(filters.dateFrom?.startOf('day')) ?? null,
    dateTo: toBackendTimestamp(filters.dateTo?.endOf('day')) ?? null,
  })

const createEmptyLogsSnapshot = (): LogsSnapshot => ({
  logs: [],
  logsPage: 0,
  logsHasMore: false,
})

const createEmptySupplierSnapshot = (): SupplierDetailsSnapshot => ({
  stores: [],
  configs: [],
  metricsByFilter: {},
  logsByFilter: {},
})

const isForbiddenError = (error: unknown) =>
  (error as { response?: { status?: number } })?.response?.status === 403

type StockImportSectionName = (typeof STOCK_IMPORT_SECTION_TABS)[number]['name']

interface StockImportsTabProps {
  activeSection?: StockImportSectionName
  onActiveSectionChange?: (section: StockImportSectionName) => void
  hideSectionToggle?: boolean
}

function StockImportsTab({
  activeSection: controlledSection,
  onActiveSectionChange,
  hideSectionToggle = false,
}: StockImportsTabProps = {}) {
  const [modal, contextHolder] = Modal.useModal()
  const [atomModal, atomModalHolder] = useAtomModal()
  const { selectedOrganisation, currentUser, tokenRoles } = useUserInfoStore(
    (state) => state
  )
  const isOrgSelected = Object.keys(selectedOrganisation).length > 0
  const currentUserRoles = (currentUser?.roles ?? []).map((currentRole) =>
    currentRole.toUpperCase()
  )
  const isStockManager =
    tokenRoles.includes('STOCK_MANAGER') ||
    currentUserRoles.includes('STOCK_MANAGER')
  const isProductSupplierUser =
    tokenRoles.includes('PRODUCT_SUPPLIER_USER') ||
    currentUserRoles.includes('PRODUCT_SUPPLIER_USER')
  const isManagedSupplierMode = isStockManager && !isProductSupplierUser

  const [suppliers, setSuppliers] = useState<ProductSupplierDto[]>([])
  const [supplierDirectoryAvailable, setSupplierDirectoryAvailable] =
    useState(true)
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>()
  const selectedSupplier = useMemo(
    () =>
      suppliers.find((supplier) => supplier.id === selectedSupplierId) ?? null,
    [suppliers, selectedSupplierId]
  )

  const [stores, setStores] = useState<StoreModel[]>([])
  const [brands, setBrands] = useState<BrandInterface[]>([])
  const [brandsLoading, setBrandsLoading] = useState(false)
  const [configs, setConfigs] = useState<StockImportConfigShortModel[]>([])
  const [logs, setLogs] = useState<StockImportLogModel[]>([])
  const [metrics, setMetrics] = useState<StockImportMetricsModel | null>(null)
  const [logFilters, setLogFilters] = useState<LogFilterState>({})
  const [logsHasMore, setLogsHasMore] = useState(false)
  const [logsLoadingMore, setLogsLoadingMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [modalDataRefreshing, setModalDataRefreshing] = useState(false)
  const [runningImportKeys, setRunningImportKeys] = useState<Set<string>>(
    new Set()
  )
  const [internalActiveSection, setInternalActiveSection] =
    useState<StockImportSectionName>('Настройки загрузки')
  const activeSection = controlledSection ?? internalActiveSection
  const setActiveSection = onActiveSectionChange ?? setInternalActiveSection

  const [supplierModalOpen, setSupplierModalOpen] = useState(false)
  const [supplierModalKey, setSupplierModalKey] = useState(0)
  const [editingSupplier, setEditingSupplier] =
    useState<ProductSupplierDto | null>(null)
  const [configModalOpen, setConfigModalOpen] = useState(false)
  const [configModalKey, setConfigModalKey] = useState(0)
  const [editingConfig, setEditingConfig] =
    useState<StockImportConfigModel | null>(null)
  const [configDetailsLoadingId, setConfigDetailsLoadingId] = useState<
    string | null
  >(null)
  const [positionConfig, setPositionConfig] =
    useState<StockImportConfigShortModel | null>(null)
  const [positionModalKey, setPositionModalKey] = useState(0)
  const [detailsLog, setDetailsLog] = useState<StockImportLogModel | null>(null)
  const [missingLog, setMissingLog] = useState<Pick<
    StockImportLogModel,
    'id'
  > | null>(null)
  const supplierSnapshotsRef = useRef<Record<string, SupplierDetailsSnapshot>>(
    {}
  )
  const modalRefreshInFlightRef = useRef(false)
  const selectedSupplierIdRef = useRef<string>()
  const logFiltersKeyRef = useRef(buildLogFiltersKey({}))
  const logsRequestVersionRef = useRef(0)
  const logsLoadingMoreRef = useRef(false)
  const detailsRequestVersionRef = useRef(0)
  const runningImportKeysRef = useRef(new Set<string>())
  const logFiltersKey = useMemo(
    () => buildLogFiltersKey(logFilters),
    [logFilters]
  )
  const isAllSuppliersMode = isAllSuppliersSelected(selectedSupplierId)

  useEffect(() => {
    selectedSupplierIdRef.current = selectedSupplierId
  }, [selectedSupplierId])

  useEffect(() => {
    logFiltersKeyRef.current = logFiltersKey
  }, [logFiltersKey])

  const applySnapshotToView = useCallback(
    (
      snapshot?: SupplierDetailsSnapshot,
      filtersKey = logFiltersKeyRef.current
    ) => {
      const logsSnapshot =
        snapshot?.logsByFilter[filtersKey] ?? createEmptyLogsSnapshot()
      setStores(snapshot?.stores ?? [])
      setConfigs(snapshot?.configs ?? [])
      setMetrics(snapshot?.metricsByFilter[filtersKey] ?? null)
      setLogs(logsSnapshot.logs)
      setLogsHasMore(logsSnapshot.logsHasMore)
    },
    []
  )

  const updateSupplierSnapshot = useCallback(
    (
      supplierId: string,
      updater: (snapshot: SupplierDetailsSnapshot) => SupplierDetailsSnapshot,
      options?: { syncView?: boolean }
    ) => {
      const currentSnapshot =
        supplierSnapshotsRef.current[supplierId] ??
        createEmptySupplierSnapshot()
      const nextSnapshot = updater(currentSnapshot)
      supplierSnapshotsRef.current = {
        ...supplierSnapshotsRef.current,
        [supplierId]: nextSnapshot,
      }

      if (
        options?.syncView !== false &&
        selectedSupplierIdRef.current === supplierId
      ) {
        applySnapshotToView(nextSnapshot)
      }

      return nextSnapshot
    },
    [applySnapshotToView]
  )

  const loadInitial = useCallback(async () => {
    setLoading(true)
    try {
      if (isProductSupplierUser) {
        setSupplierDirectoryAvailable(false)
        setSuppliers([])
        setSelectedSupplierId(SELF_SUPPLIER_SNAPSHOT_KEY)
        return
      }

      if (!isManagedSupplierMode) {
        setSupplierDirectoryAvailable(false)
        setSuppliers([])
        setSelectedSupplierId(undefined)
        return
      }

      const supplierList = await stockImportsApi.getProductSuppliers()
      setSupplierDirectoryAvailable(true)
      setSuppliers(supplierList)
      setSelectedSupplierId((prev) => prev ?? ALL_SUPPLIERS_SNAPSHOT_KEY)
    } catch (error) {
      if (isForbiddenError(error)) {
        setSupplierDirectoryAvailable(false)
        setSuppliers([])
        setSelectedSupplierId(SELF_SUPPLIER_SNAPSHOT_KEY)
        toast.error(
          'Нет доступа к справочнику поставщиков. Журнал загрузок открыт без выбора поставщика.'
        )
        return
      }

      handleApiError(error, modal)
    } finally {
      setLoading(false)
    }
  }, [isManagedSupplierMode, isProductSupplierUser, modal])

  const ensureBrandsLoaded = useCallback(async () => {
    if (brands.length > 0 || brandsLoading) return

    setBrandsLoading(true)
    try {
      const brandList = await fetchBrands()
      setBrands(brandList)
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setBrandsLoading(false)
    }
  }, [brands.length, brandsLoading, modal])

  const loadConfigsAndMetrics = useCallback(
    async (supplierId: string, options?: { force?: boolean }) => {
      const requestVersion = ++detailsRequestVersionRef.current
      const force = options?.force ?? false
      const requestSupplierId = resolveRequestSupplierId(
        supplierId,
        isProductSupplierUser
      )
      const filtersKey = buildLogFiltersKey(logFilters)
      const cachedSnapshot = supplierSnapshotsRef.current[supplierId]
      const hasCachedMetrics =
        !!cachedSnapshot && filtersKey in cachedSnapshot.metricsByFilter

      if (!force && cachedSnapshot && hasCachedMetrics) {
        applySnapshotToView(cachedSnapshot, filtersKey)
        setDetailsLoading(false)
        return
      }

      applySnapshotToView(cachedSnapshot, filtersKey)
      setDetailsLoading(true)
      try {
        const metricsParams = buildAnalyticsParams(
          requestSupplierId,
          logFilters
        )

        if (!force && cachedSnapshot) {
          const metricData = await stockImportsApi.getMetrics(metricsParams)
          if (requestVersion !== detailsRequestVersionRef.current) return
          const nextSnapshot = updateSupplierSnapshot(
            supplierId,
            (snapshot) => ({
              ...snapshot,
              metricsByFilter: {
                ...snapshot.metricsByFilter,
                [filtersKey]: metricData,
              },
            }),
            { syncView: false }
          )

          if (
            selectedSupplierIdRef.current === supplierId &&
            logFiltersKeyRef.current === filtersKey
          ) {
            applySnapshotToView(nextSnapshot, filtersKey)
          }
          return
        }

        const cachedStores = cachedSnapshot?.stores ?? []
        const cachedConfigs = cachedSnapshot?.configs ?? []
        const [storeResult, configResult, metricResult] =
          await Promise.allSettled([
            stockImportsApi.getStores({ force }),
            stockImportsApi.getConfigs(requestSupplierId),
            stockImportsApi.getMetrics(metricsParams, { force }),
          ])
        if (requestVersion !== detailsRequestVersionRef.current) return

        const nextSnapshot = updateSupplierSnapshot(
          supplierId,
          (snapshot) => ({
            ...snapshot,
            stores: pickSettledValue(storeResult, cachedStores),
            configs: pickSettledValue(configResult, cachedConfigs),
            metricsByFilter:
              metricResult.status === 'fulfilled'
                ? {
                    ...snapshot.metricsByFilter,
                    [filtersKey]: metricResult.value,
                  }
                : snapshot.metricsByFilter,
          }),
          { syncView: false }
        )

        if (
          selectedSupplierIdRef.current === supplierId &&
          logFiltersKeyRef.current === filtersKey
        ) {
          applySnapshotToView(nextSnapshot, filtersKey)
        }

        const settledError = findSettledError([
          storeResult,
          configResult,
          metricResult,
        ])
        if (settledError) handleApiError(settledError, modal)
      } catch (error) {
        handleApiError(error, modal)
      } finally {
        if (requestVersion === detailsRequestVersionRef.current)
          setDetailsLoading(false)
      }
    },
    [
      applySnapshotToView,
      isProductSupplierUser,
      logFilters,
      modal,
      updateSupplierSnapshot,
    ]
  )

  const applyUpdatedConfig = useCallback(
    (supplierId: string, updatedConfig: StockImportConfigShortModel) => {
      updateSupplierSnapshot(supplierId, (snapshot) => {
        const existingIndex = snapshot.configs.findIndex(
          (item) => item.id === updatedConfig.id
        )
        if (existingIndex === -1) {
          return { ...snapshot, configs: [...snapshot.configs, updatedConfig] }
        }

        const nextConfigs = [...snapshot.configs]
        nextConfigs[existingIndex] = updatedConfig
        return { ...snapshot, configs: nextConfigs }
      })
    },
    [updateSupplierSnapshot]
  )

  // Re-syncs THIS component's own stores state with stockImportsApi's shared cache right before the
  // config modal opens. No `force` here on purpose: updateStore/createStore/
  // updateProductSupplier already patch (or invalidate) that shared cache the instant the mutation
  // happens, from wherever it happens — including the sibling "Управление складами" tab, which keeps
  // this component mounted. What they can't do is push the update into THIS component's React state
  // (a plain module variable changing doesn't trigger a re-render), so we still need to re-read it —
  // but stockImportsApi.getStores() with no force just returns the already-current cache, no network
  // call needed in the common case. Forcing here would only help if the store was edited by a
  // different session entirely (another tab/user/direct backend change) — a rare enough case that
  // trading it for a guaranteed extra request on every single modal open isn't worth it.
  const refreshStoresForModal = useCallback(
    async (supplierId: string) => {
      if (modalRefreshInFlightRef.current) return
      modalRefreshInFlightRef.current = true

      setModalDataRefreshing(true)
      try {
        const storeList = await stockImportsApi.getStores()
        updateSupplierSnapshot(supplierId, (snapshot) => ({
          ...snapshot,
          stores: storeList,
        }))
      } catch (error) {
        handleApiError(error, modal)
      } finally {
        modalRefreshInFlightRef.current = false
        setModalDataRefreshing(false)
      }
    },
    [modal, updateSupplierSnapshot]
  )

  const loadLogs = useCallback(
    async (supplierId: string, page = 0, options?: { force?: boolean }) => {
      const requestVersion = ++logsRequestVersionRef.current
      setLogsLoadingMore(false)
      const force = options?.force ?? false
      const requestSupplierId = resolveRequestSupplierId(
        supplierId,
        isProductSupplierUser
      )
      const filtersKey = buildLogFiltersKey(logFilters)
      const cachedSnapshot = supplierSnapshotsRef.current[supplierId]
      const cachedLogs = cachedSnapshot?.logsByFilter[filtersKey]

      if (!force && cachedLogs) applySnapshotToView(cachedSnapshot, filtersKey)

      setDetailsLoading(true)
      try {
        const logsParams = buildAnalyticsParams(
          requestSupplierId,
          logFilters,
          page
        )
        const logsPageData = await stockImportsApi.getLogs(logsParams, {
          force,
        })
        if (requestVersion !== logsRequestVersionRef.current) return
        const logsSnapshot: LogsSnapshot = {
          logs: logsPageData.content,
          logsPage: page,
          logsHasMore: logsPageData.content.length < logsPageData.totalElements,
          newestRunStartedAt: findNewestRunStartedAt(logsPageData.content),
        }

        const nextSnapshot = updateSupplierSnapshot(
          supplierId,
          (snapshot) => ({
            ...snapshot,
            logsByFilter: {
              ...snapshot.logsByFilter,
              [filtersKey]: logsSnapshot,
            },
          }),
          { syncView: false }
        )

        if (
          requestVersion === logsRequestVersionRef.current &&
          selectedSupplierIdRef.current === supplierId &&
          logFiltersKeyRef.current === filtersKey
        ) {
          applySnapshotToView(nextSnapshot, filtersKey)
        }
      } catch (error) {
        handleApiError(error, modal)
      } finally {
        if (requestVersion === logsRequestVersionRef.current)
          setDetailsLoading(false)
      }
    },
    [
      applySnapshotToView,
      isProductSupplierUser,
      logFilters,
      modal,
      updateSupplierSnapshot,
    ]
  )

  useEffect(() => {
    loadInitial()
  }, [loadInitial])

  useEffect(() => {
    if (selectedSupplierId) loadConfigsAndMetrics(selectedSupplierId)
  }, [selectedSupplierId, loadConfigsAndMetrics, logFiltersKey])

  useEffect(() => {
    if (!selectedSupplierId || activeSection !== 'Журнал загрузок') return
    loadLogs(selectedSupplierId, 0)
  }, [selectedSupplierId, activeSection, loadLogs])

  const handleSupplierChange = useCallback((supplierId: string) => {
    if (isAllSuppliersSelected(supplierId)) {
      supplierSnapshotsRef.current = Object.fromEntries(
        Object.entries(supplierSnapshotsRef.current).filter(
          ([key]) => key !== supplierId
        )
      )
    }
    setSelectedSupplierId(supplierId)
    setLogFilters({})
  }, [])

  const openConfigModal = useCallback(
    async (config: Pick<StockImportConfigShortModel, 'id'> | null) => {
      if (config && configDetailsLoadingId) return

      let fullConfig: StockImportConfigModel | null = null
      if (config) {
        setConfigDetailsLoadingId(config.id)
        try {
          fullConfig = await stockImportsApi.getConfig(config.id)
        } catch (error) {
          handleApiError(error, modal)
          return
        } finally {
          setConfigDetailsLoadingId(null)
        }
        if (selectedSupplierIdRef.current)
          applyUpdatedConfig(selectedSupplierIdRef.current, fullConfig)
      }

      setEditingConfig(fullConfig)
      setConfigModalKey((key) => key + 1)
      setConfigModalOpen(true)
      void ensureBrandsLoaded()
      if (selectedSupplierId) void refreshStoresForModal(selectedSupplierId)
    },
    [
      applyUpdatedConfig,
      configDetailsLoadingId,
      ensureBrandsLoaded,
      modal,
      selectedSupplierId,
      refreshStoresForModal,
    ]
  )

  const updateLogFilters = (filters: Partial<LogFilterState>) => {
    setLogFilters((prev) => ({ ...prev, ...filters }))
  }

  const loadMoreLogs = useCallback(async () => {
    if (
      !selectedSupplierId ||
      logsLoadingMoreRef.current ||
      detailsLoading ||
      !logsHasMore
    )
      return

    const requestVersion = logsRequestVersionRef.current
    const currentFiltersKey = buildLogFiltersKey(logFilters)
    const currentLogsSnapshot =
      supplierSnapshotsRef.current[selectedSupplierId]?.logsByFilter[
        currentFiltersKey
      ]
    if (!currentLogsSnapshot?.logsHasMore) return

    const nextPage = currentLogsSnapshot.logsPage + 1
    const { newestRunStartedAt } = currentLogsSnapshot
    const requestSupplierId = resolveRequestSupplierId(
      selectedSupplierId,
      isProductSupplierUser
    )
    logsLoadingMoreRef.current = true
    setLogsLoadingMore(true)
    try {
      const logsPageData = await stockImportsApi.getLogs(
        buildAnalyticsParams(
          requestSupplierId,
          logFilters,
          nextPage,
          newestRunStartedAt
        )
      )
      if (requestVersion !== logsRequestVersionRef.current) return
      const existingLogs =
        supplierSnapshotsRef.current[selectedSupplierId]?.logsByFilter[
          currentFiltersKey
        ]?.logs ?? []
      const nextLogs = appendUniqueLogs(
        existingLogs,
        logsPageData.content,
        newestRunStartedAt
      )
      const nextLogsHasMore =
        logsPageData.content.length > 0 &&
        (nextPage + 1) * LOGS_PAGE_SIZE < logsPageData.totalElements

      updateSupplierSnapshot(
        selectedSupplierId,
        (snapshot) => ({
          ...snapshot,
          logsByFilter: {
            ...snapshot.logsByFilter,
            [currentFiltersKey]: {
              logs: nextLogs,
              logsPage: nextPage,
              logsHasMore: nextLogsHasMore,
              newestRunStartedAt,
            },
          },
        }),
        { syncView: false }
      )

      if (
        requestVersion === logsRequestVersionRef.current &&
        selectedSupplierIdRef.current === selectedSupplierId &&
        logFiltersKeyRef.current === currentFiltersKey
      ) {
        setLogs(nextLogs)
        setLogsHasMore(nextLogsHasMore)
      }
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      logsLoadingMoreRef.current = false
      if (requestVersion === logsRequestVersionRef.current)
        setLogsLoadingMore(false)
    }
  }, [
    detailsLoading,
    isProductSupplierUser,
    logFilters,
    logsHasMore,
    modal,
    selectedSupplierId,
    updateSupplierSnapshot,
  ])

  const showExecutionResult = (
    title: string,
    result: Omit<StockImportExecutionResponse, 'updatedConfig'>
  ) => {
    const openResultModal = result.success
      ? atomModal.success
      : atomModal.warning
    const failedDetails = result.failedDetails ?? []
    const failedCount = result.failedCount ?? failedDetails.length
    const logId = result.logId
    const resultModalRef: { instance?: { close: () => void } } = {}

    const handleViewAllDetails = () => {
      if (!logId) return
      resultModalRef.instance?.close()
      setMissingLog({ id: logId })
    }

    const failedDetailsColumns: StockImportsTableColumn<
      NonNullable<StockImportExecutionResponse['failedDetails']>[number]
    >[] = [
      {
        id: 'rowIndex',
        header: 'Строка',
        width: 'minmax(72px, 0.5fr)',
        cell: ({ row }) => toExcelRowNumber(row.original.rowIndex),
      },
      {
        id: 'article',
        header: 'Артикул (производителя)',
        width: 'minmax(150px, 1.1fr)',
        cell: ({ row }) => row.original.article || '—',
      },
      {
        id: 'articleInch',
        header: 'Артикул (поставщика)',
        width: 'minmax(150px, 1.1fr)',
        cell: ({ row }) => row.original.articleInch || '—',
      },
      {
        id: 'barcode',
        header: 'Штрихкод',
        width: 'minmax(120px, 0.9fr)',
        cell: ({ row }) => row.original.barcode || '—',
      },
      {
        id: 'brand',
        header: 'Бренд',
        width: 'minmax(110px, 0.8fr)',
        cell: ({ row }) => row.original.brand || '—',
      },
      {
        id: 'description',
        header: 'Наименование',
        width: 'minmax(260px, 1.8fr)',
        cell: ({ row }) => row.original.description || '—',
      },
      {
        id: 'reason',
        header: 'Почему не загружено',
        width: 'minmax(320px, 2fr)',
        cell: ({ row }) => {
          const reason =
            formatMissingSkuReason(
              row.original.reason,
              row.original.searchedBy
            ) || 'Причина не указана'
          return reason
        },
      },
    ]

    resultModalRef.instance = openResultModal({
      title,
      okText: 'Понятно',
      width: failedCount > 0 ? 'min(1080px, calc(100vw - 48px))' : undefined,
      content: (
        <div className={cn.executionResult}>
          <div className={cn.executionMessage}>{result.message}</div>
          <div className={cn.executionSummary}>
            <div className={cn.executionSummaryItem}>
              <span>Загружено</span>
              <strong>{result.loadedCount ?? 0}</strong>
            </div>
            <div className={cn.executionSummaryItem}>
              <span>Не загружено</span>
              <strong>{failedCount}</strong>
            </div>
          </div>

          {failedCount > 0 && (
            <div className={cn.executionDetails}>
              <div className={cn.executionDetailsHeader}>
                <strong>Незагруженные позиции</strong>
                <div className={cn.executionDetailsHeaderActions}>
                  <span>
                    {failedDetails.length > 0
                      ? `Показано: ${failedDetails.length}`
                      : 'Детализация не вернулась от сервера'}
                  </span>
                  {logId && (
                    <Button size="small" onClick={handleViewAllDetails}>
                      Смотреть все детали
                    </Button>
                  )}
                </div>
              </div>
              {failedDetails.length > 0 ? (
                <StockImportsVirtualTable
                  data={failedDetails}
                  columns={failedDetailsColumns}
                  rowKey={(row, index) =>
                    `${row.rowIndex}-${row.article ?? row.description ?? 'row'}-${index}`
                  }
                  emptyText="Нет незагруженных позиций"
                  minTableWidth="100%"
                  estimateRowHeight={96}
                  height={360}
                  organisationSelected={isOrgSelected}
                />
              ) : (
                <div className={cn.executionEmptyDetails}>
                  Сервер сообщил о незагруженных позициях, но не передал список
                  причин.
                </div>
              )}
            </div>
          )}
        </div>
      ),
    })
  }

  const handleSupplierSaved = (supplier: ProductSupplierDto) => {
    setSupplierModalOpen(false)
    setEditingSupplier(null)
    setSuppliers((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === supplier.id)
      if (existingIndex === -1) return [...prev, supplier]

      const next = [...prev]
      next[existingIndex] = supplier
      return next
    })
    setSelectedSupplierId(supplier.id)
  }

  const handleSupplierStoresChanged = useCallback(async () => {
    try {
      const storeList = await stockImportsApi.getStores({ force: true })
      setStores(storeList)

      const supplierId = selectedSupplierIdRef.current
      if (supplierId) {
        updateSupplierSnapshot(supplierId, (snapshot) => ({
          ...snapshot,
          stores: storeList,
        }))
      }
    } catch (error) {
      handleApiError(error, modal)
    }
  }, [modal, updateSupplierSnapshot])

  const runImport = async (
    config: StockImportConfigShortModel,
    mode: StockImportRunMode
  ): Promise<boolean> => {
    const runSupplierId = selectedSupplierId
    const runKey = `${config.id}:${mode}`
    if (!acquireStockImportRun(runningImportKeysRef.current, runKey))
      return false
    setRunningImportKeys((previous) => new Set(previous).add(runKey))

    const isTrial = mode === 'trial'
    const resultTitle = isTrial
      ? 'Результат пробного запуска'
      : 'Результат запуска с сохранением'
    const toastId = toast.loading(
      isTrial
        ? STOCK_IMPORT_PROGRESS_MESSAGES.trial
        : STOCK_IMPORT_PROGRESS_MESSAGES.manual
    )
    try {
      const result = await stockImportsApi.runImport(config.id, mode)
      if (result.success) toast.success(result.message, { id: toastId })
      else toast.error(result.message, { id: toastId })
      showExecutionResult(resultTitle, result)

      if (runSupplierId && result.updatedConfig) {
        applyUpdatedConfig(runSupplierId, result.updatedConfig)
      }
      return true
    } catch (error) {
      toast.dismiss(toastId)
      const executionResult = extractExecutionResponse(error)
      if (executionResult) {
        toast.error(executionResult.message)
        showExecutionResult(resultTitle, executionResult)
        if (runSupplierId && executionResult.updatedConfig) {
          applyUpdatedConfig(runSupplierId, executionResult.updatedConfig)
        }
        return true
      }
      handleApiError(error, modal)
      return false
    } finally {
      if (runSupplierId) {
        updateSupplierSnapshot(
          runSupplierId,
          invalidateLogsAndMetricsSnapshot,
          { syncView: false }
        )
        await loadConfigsAndMetrics(runSupplierId, { force: true })
        if (activeSection === 'Журнал загрузок')
          await loadLogs(runSupplierId, 0, { force: true })
      }
      runningImportKeysRef.current.delete(runKey)
      setRunningImportKeys((previous) => {
        const next = new Set(previous)
        next.delete(runKey)
        return next
      })
    }
  }

  const runRealImportWithConfirm = (config: StockImportConfigShortModel) => {
    atomModal.confirm({
      ...STOCK_IMPORT_LIVE_CONFIRMATION,
      onOk: () => {
        void runImport(config, 'live')
      },
    })
  }

  const openReportsForConfig = (config: StockImportConfigShortModel) => {
    setActiveSection('Журнал загрузок')
    setLogFilters({ configId: config.id })
  }

  const toggleArchiveConfig = (
    config: StockImportConfigShortModel,
    isArchived: boolean
  ) => {
    if (!selectedSupplierId) return
    atomModal.confirm({
      title: isArchived
        ? 'Перевести настройку в архив?'
        : 'Восстановить настройку из архива?',
      content: isArchived
        ? getArchiveConfigWarning(config.store?.description)
        : `Настройка для склада «${config.store?.description ?? '—'}» снова станет доступна для запусков.`,
      okText: isArchived ? 'В архив' : 'Восстановить',
      danger: isArchived,
      onOk: async () => {
        const toastId = toast.loading(
          isArchived ? 'Архивация настройки...' : 'Восстановление настройки...'
        )
        try {
          const updatedConfig = await stockImportsApi.archiveConfig(
            config.id,
            isArchived
          )
          toast.success(
            isArchived
              ? 'Настройка переведена в архив'
              : 'Настройка восстановлена из архива',
            { id: toastId }
          )
          updateSupplierSnapshot(selectedSupplierId, (snapshot) => ({
            ...snapshot,
            configs: snapshot.configs.map((item) =>
              item.id === updatedConfig.id ? updatedConfig : item
            ),
          }))
        } catch (error) {
          toast.dismiss(toastId)
          if (handleStockImportsFormError(error)) return
          handleApiError(error, modal)
        }
      },
    })
  }

  const resetConfigChecks = (config: StockImportConfigShortModel) => {
    if (!selectedSupplierId) return
    atomModal.confirm({
      title: 'Сбросить историю проверок?',
      content: RESET_CHECKS_WARNING,
      okText: 'Сбросить',
      onOk: async () => {
        const toastId = toast.loading('Сброс истории проверок...')
        try {
          const updatedConfig = await stockImportsApi.resetChecks(config.id)
          toast.success('История проверок сброшена', { id: toastId })
          updateSupplierSnapshot(selectedSupplierId, (snapshot) => ({
            ...snapshot,
            configs: snapshot.configs.map((item) =>
              item.id === updatedConfig.id ? updatedConfig : item
            ),
          }))
        } catch (error) {
          toast.dismiss(toastId)
          handleApiError(error, modal)
        }
      },
    })
  }

  const supplierOptions = buildSupplierOptions(suppliers)
  const configsById = new Map(configs.map((config) => [config.id, config]))
  const actionIconClassName = classNames(
    cn.actionIcon,
    isOrgSelected && cn.accentSelected
  )
  const accentLinkClassName = classNames(
    cn.accentLink,
    isOrgSelected && cn.accentSelected
  )

  const hasStockImportsScope = isManagedSupplierMode || isProductSupplierUser
  const hasSelectedContext =
    isProductSupplierUser ||
    selectedSupplierId === SELF_SUPPLIER_SNAPSHOT_KEY ||
    isAllSuppliersMode ||
    !!selectedSupplier
  const canAddConfig =
    !loading &&
    hasStockImportsScope &&
    hasSelectedContext &&
    activeSection === 'Настройки загрузки'

  const configColumns: StockImportsTableColumn<StockImportConfigShortModel>[] =
    [
      {
        id: 'store',
        header: 'Склад',
        width: 'minmax(160px, 1.2fr)',
        cell: ({ row }) => (
          <>
            {row.original.store?.description ?? '—'}
            {row.original.pricesEnabled && (
              <Tag className={cn.pricesTag} color="processing">
                Цены из файла
              </Tag>
            )}
          </>
        ),
      },
      ...(isAllSuppliersMode
        ? [
            {
              id: 'supplier',
              header: 'Поставщик',
              width: 'minmax(160px, 1.2fr)',
              cell: ({
                row,
              }: {
                row: { original: StockImportConfigShortModel }
              }) => row.original.productSupplier?.description ?? '—',
            },
          ]
        : []),
      {
        id: 'status',
        header: () =>
          columnHeaderWithTooltip('Статус', CONFIG_STATUS_TOOLTIP_ITEMS),
        width: '150px',
        cell: ({ row }) => (
          <Tag
            className={cn.tableStatusTag}
            color={statusMap[row.original.status].color}
          >
            {statusMap[row.original.status].label}
          </Tag>
        ),
      },
      {
        id: 'lastRun',
        header: () =>
          columnHeaderWithTooltip('Последний запуск', RUN_STATUS_TOOLTIP_ITEMS),
        width: '160px',
        cell: ({ row }) =>
          formatLastRunCell(
            row.original.lastRunAt,
            row.original.lastRunStatus,
            row.original.lastRunType
          ),
      },
      {
        id: 'sourceType',
        header: 'Источник',
        width: 'minmax(190px, 1fr)',
        cell: ({ row }) => sourceTypeLabels[row.original.sourceType],
      },
      {
        id: 'fileFormat',
        header: 'Формат',
        width: '100px',
        cell: ({ row }) => row.original.fileFormat ?? '—',
      },
      {
        id: 'cronExpression',
        header: 'Как часто проверять',
        width: 'minmax(150px, 1fr)',
        cell: ({ row }) => formatCronExpression(row.original.cronExpression),
      },
      {
        id: 'isStockExpired',
        header: 'Просрочено',
        width: '120px',
        cell: ({ row }) =>
          row.original.isStockExpired ? <Tag color="error">Да</Tag> : 'Нет',
      },
      {
        id: 'configActions',
        header: 'Действия',
        width: '290px',
        cell: ({ row }) => (
          <Space size={6}>
            <Tooltip title="Редактировать">
              <Button
                icon={<EditOutlined />}
                size="small"
                shape="circle"
                className={actionIconClassName}
                loading={configDetailsLoadingId === row.original.id}
                onClick={() => void openConfigModal(row.original)}
              />
            </Tooltip>
            {isStockImportTrialRunAllowed(row.original.status) && (
              <Tooltip title="Пробный запуск: без перезаписи остатков, после проверки результата запустите загрузку с сохранением">
                <Button
                  icon={<ExperimentOutlined />}
                  size="small"
                  shape="circle"
                  className={actionIconClassName}
                  loading={runningImportKeys.has(`${row.original.id}:trial`)}
                  onClick={() => runImport(row.original, 'trial')}
                  disabled={
                    runningImportKeys.has(`${row.original.id}:trial`) ||
                    runningImportKeys.has(`${row.original.id}:live`)
                  }
                />
              </Tooltip>
            )}
            <Tooltip title="Запустить и сохранить: перезапишет остатки склада">
              <Button
                icon={<PlayCircleOutlined />}
                size="small"
                shape="circle"
                className={actionIconClassName}
                loading={runningImportKeys.has(`${row.original.id}:live`)}
                onClick={() => runRealImportWithConfirm(row.original)}
                disabled={
                  !isStockImportLiveRunAllowed(row.original.status) ||
                  runningImportKeys.has(`${row.original.id}:trial`) ||
                  runningImportKeys.has(`${row.original.id}:live`)
                }
              />
            </Tooltip>
            <Tooltip title="Отчёты о загрузках">
              <Button
                icon={<FileSearchOutlined />}
                size="small"
                shape="circle"
                className={actionIconClassName}
                onClick={() => openReportsForConfig(row.original)}
              />
            </Tooltip>
            <Tooltip title="Проверить артикул">
              <Button
                icon={<SearchOutlined />}
                size="small"
                shape="circle"
                className={actionIconClassName}
                onClick={() => {
                  setPositionModalKey((key) => key + 1)
                  setPositionConfig(row.original)
                }}
              />
            </Tooltip>
            <Tooltip title="Сбросить историю проверок">
              <Button
                icon={<HistoryOutlined />}
                size="small"
                shape="circle"
                className={actionIconClassName}
                onClick={() => resetConfigChecks(row.original)}
              />
            </Tooltip>
            {row.original.status === 'ARCHIVED' ? (
              <Tooltip title="Восстановить из архива">
                <Button
                  icon={<UndoOutlined />}
                  size="small"
                  shape="circle"
                  className={actionIconClassName}
                  onClick={() => toggleArchiveConfig(row.original, false)}
                />
              </Tooltip>
            ) : (
              <Tooltip title="В архив">
                <Button
                  icon={<StopOutlined />}
                  size="small"
                  shape="circle"
                  className={actionIconClassName}
                  onClick={() => toggleArchiveConfig(row.original, true)}
                />
              </Tooltip>
            )}
          </Space>
        ),
      },
    ]

  const logColumns: StockImportsTableColumn<StockImportLogModel>[] = [
    {
      id: 'runStatus',
      header: () => columnHeaderWithTooltip('Статус', RUN_STATUS_TOOLTIP_ITEMS),
      width: '150px',
      cell: ({ row }) => (
        <Tag
          className={cn.tableStatusTag}
          color={runStatusMap[row.original.runStatus].color}
        >
          {runStatusMap[row.original.runStatus].label}
        </Tag>
      ),
    },
    {
      id: 'runKind',
      header: 'Режим загрузки',
      width: '140px',
      cell: ({ row }) =>
        row.original.isTrial ? (
          <Tag className={cn.tableStatusTag} color="purple">
            Тестовый
          </Tag>
        ) : (
          <Tag className={cn.tableStatusTag}>Боевой</Tag>
        ),
    },
    {
      id: 'runType',
      header: 'Инициатор',
      width: '130px',
      cell: ({ row }) => runTypeLabels[row.original.runType] ?? '—',
    },
    {
      id: 'runPeriod',
      header: 'Начало и окончание',
      width: '180px',
      cell: ({ row }) =>
        formatRunPeriodCell(row.original.runStartedAt, row.original.runEndedAt),
    },
    ...(isAllSuppliersMode
      ? [
          {
            id: 'supplier',
            header: 'Поставщик',
            width: 'minmax(160px, 1fr)',
            cell: ({ row }: { row: { original: StockImportLogModel } }) => {
              const label = (
                row.original.productSupplier ??
                configsById.get(row.original.configId)?.productSupplier
              )?.description
              if (!label) return '—'
              return (
                <span className={tableCn.cellEllipsis} title={label}>
                  {label}
                </span>
              )
            },
          },
        ]
      : []),
    {
      id: 'store',
      header: 'Склад',
      width: 'minmax(160px, 1fr)',
      cell: ({ row }) => {
        const config = configsById.get(row.original.configId)
        const storeName = (row.original.store ?? config?.store)?.description
        if (!storeName) return '—'
        const sourceLabel = config ? sourceTypeLabels[config.sourceType] : null
        return (
          <div className={cn.logFileCell}>
            <span className={tableCn.cellEllipsis} title={storeName}>
              {storeName}
            </span>
            {sourceLabel && (
              <span className={cn.logStoreSource}>{sourceLabel}</span>
            )}
          </div>
        )
      },
    },
    {
      id: 'fileName',
      header: 'Файл',
      width: 'minmax(160px, 1.2fr)',
      cell: ({ row }) => {
        const { fileName, fileHash } = row.original
        if (!fileName) return '—'
        const truncatedHash = truncateFileHash(fileHash)
        return (
          <div className={cn.logFileCell}>
            <span className={tableCn.cellEllipsis} title={fileName}>
              {fileName}
            </span>
            {truncatedHash && (
              <Tooltip title={fileHash}>
                <span className={cn.logFileHash}>({truncatedHash})</span>
              </Tooltip>
            )}
          </div>
        )
      },
    },
    ...LOG_COUNTERS.map(
      ({ field, label, hint }): StockImportsTableColumn<StockImportLogModel> =>
        field === 'hasMissingSkuData'
          ? {
              id: field,
              header: () => labelWithTooltip(label, hint),
              width: '90px',
              cell: ({ row }) => <MissingSkuReportMark log={row.original} />,
            }
          : {
              id: field,
              header: () => labelWithTooltip(label, hint),
              width: field === 'missingRows' ? '140px' : '120px',
              cell: ({ row }) => row.original[field],
            }
    ),
    {
      id: 'parsingTimeMs',
      header: 'Продолжительность',
      width: '150px',
      cell: ({ row }) => formatMs(row.original.parsingTimeMs),
    },
    {
      id: 'errorReason',
      header: 'Пояснение',
      width: 'minmax(180px, 1.4fr)',
      cell: ({ row }) =>
        row.original.errorReason ? (
          <Tooltip title={row.original.errorReason}>
            <span className={tableCn.cellEllipsis}>
              {row.original.errorReason}
            </span>
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      id: 'config',
      header: 'Настройка',
      width: '110px',
      cell: ({ row }) => (
        <Button
          type="link"
          size="small"
          className={accentLinkClassName}
          onClick={(event) => {
            event.stopPropagation()
            void openConfigModal({ id: row.original.configId })
          }}
        >
          Открыть
        </Button>
      ),
    },
  ]

  const showSupplierSelect = isManagedSupplierMode && supplierDirectoryAvailable
  const isSettingsSection = activeSection === 'Настройки загрузки'
  const supplierSelect = (
    <HoverElement>
      <AtomSelect
        openByHover={false}
        outlined
        showSearch
        allowClear={false}
        placeholder="Выберите поставщика"
        value={selectedSupplierId}
        options={supplierOptions}
        loading={loading}
        isOrganisationSelected={isOrgSelected}
        handleChange={(supplierId) => {
          if (typeof supplierId === 'string') handleSupplierChange(supplierId)
        }}
      />
    </HoverElement>
  )

  return (
    <div className={cn.stockImportsTab}>
      {contextHolder}
      {atomModalHolder}

      {(isSettingsSection || !hasSelectedContext) &&
      (showSupplierSelect || canAddConfig) ? (
        <div className={cn.toolbar}>
          {showSupplierSelect && (
            <>
              <div className={cn.supplierSelect}>{supplierSelect}</div>
              {isSettingsSection && (
                <>
                  <AtomButton
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setEditingSupplier(null)
                      setSupplierModalKey((key) => key + 1)
                      setSupplierModalOpen(true)
                    }}
                  >
                    Поставщик
                  </AtomButton>
                  <AtomButton
                    variant="secondary"
                    icon={<EditOutlined />}
                    disabled={!selectedSupplier}
                    onClick={() => {
                      setEditingSupplier(selectedSupplier)
                      setSupplierModalKey((key) => key + 1)
                      setSupplierModalOpen(true)
                    }}
                  >
                    Редактировать
                  </AtomButton>
                </>
              )}
            </>
          )}
          {canAddConfig && (
            <AtomButton
              className={cn.toolbarEnd}
              icon={<PlusOutlined />}
              disabled={stores.length === 0}
              onClick={() => void openConfigModal(null)}
            >
              Добавить настройку
            </AtomButton>
          )}
        </div>
      ) : null}

      {loading ? (
        <Loader size={24} />
      ) : !hasStockImportsScope ? (
        <div className={cn.emptyState}>
          Раздел доступен менеджеру загрузки остатков или пользователю
          поставщика
        </div>
      ) : !hasSelectedContext ? (
        <div className={cn.emptyState}>
          Создайте или выберите поставщика для настройки загрузки остатков
        </div>
      ) : (
        <>
          {activeSection !== 'Настройки загрузки' && (
            <div className={cn.metricsGrid}>
              <div className={cn.metricItem}>
                <span>Запусков</span>
                <strong>{metrics?.totalRuns ?? 0}</strong>
              </div>
              <div className={cn.metricItem}>
                <span>Успешность</span>
                <strong>
                  {metrics ? `${metrics.successRate.toFixed(1)}%` : '—'}
                </strong>
              </div>
              <div className={cn.metricItem}>
                <span>Ошибок</span>
                <strong>{metrics?.errorCount ?? 0}</strong>
              </div>
              <div className={cn.metricItem}>
                <span>Среднее время</span>
                <strong>{formatMs(metrics?.avgExecutionTimeMs)}</strong>
              </div>
              <div className={cn.metricItem}>
                <span>Missing SKU</span>
                <strong>
                  {metrics
                    ? `${metrics.avgMissingPercentage.toFixed(1)}%`
                    : '—'}
                </strong>
              </div>
            </div>
          )}

          {!hideSectionToggle && (
            <div className={cn.sectionTabs}>
              <AtomSwitch
                options={STOCK_IMPORT_SECTION_TABS.map((tab) => ({
                  value: tab.name,
                  label: tab.name,
                }))}
                value={activeSection}
                onChange={setActiveSection}
                ariaLabel="Загрузка остатков"
                isOrganisationSelected={isOrgSelected}
              />
            </div>
          )}

          <div className={cn.sectionContent}>
            {activeSection === 'Настройки загрузки' && (
              <AtomTable
                ariaLabel="Настройки загрузки"
                data={configs}
                columns={configColumns}
                getRowId={(row) => row.id}
                columnWidths={getColumnWidths(configColumns)}
                isOrganisationSelected={isOrgSelected}
                isStale={detailsLoading}
                emptyContent={
                  detailsLoading ? (
                    <Loader size={24} />
                  ) : stores.length === 0 ? (
                    'Сначала создайте склад'
                  ) : undefined
                }
              />
            )}

            {activeSection === 'Журнал загрузок' && (
              <>
                <div className={cn.logsFilters}>
                  {showSupplierSelect && (
                    <div className={cn.logSupplierFilter}>{supplierSelect}</div>
                  )}
                  <div className={cn.logFilter}>
                    <JournalConfigFilter
                      configs={configs}
                      value={logFilters.configId}
                      withSupplier={isAllSuppliersMode}
                      isOrganisationSelected={isOrgSelected}
                      onChange={(configId) => updateLogFilters({ configId })}
                    />
                  </div>
                  <div className={cn.logFilter}>
                    <HoverElement>
                      <AtomSelect
                        openByHover={false}
                        outlined
                        allowClear
                        placeholder="Статус"
                        value={logFilters.runStatus}
                        options={RUN_STATUS_OPTIONS}
                        isOrganisationSelected={isOrgSelected}
                        handleChange={(runStatus) => {
                          if (
                            typeof runStatus === 'string' &&
                            isRunStatus(runStatus)
                          )
                            updateLogFilters({ runStatus })
                        }}
                        handleClear={() =>
                          updateLogFilters({ runStatus: undefined })
                        }
                      />
                    </HoverElement>
                  </div>
                  <div className={cn.logFilter}>
                    <HoverElement>
                      <AtomSelect
                        openByHover={false}
                        outlined
                        allowClear
                        placeholder="Режим загрузки"
                        value={logFilters.runKind}
                        options={RUN_KIND_OPTIONS}
                        isOrganisationSelected={isOrgSelected}
                        handleChange={(runKind) => {
                          if (runKind === 'LIVE' || runKind === 'TRIAL')
                            updateLogFilters({ runKind })
                        }}
                        handleClear={() =>
                          updateLogFilters({ runKind: undefined })
                        }
                      />
                    </HoverElement>
                  </div>
                  <div className={cn.logFilter}>
                    <HoverElement>
                      <AtomSelect
                        openByHover={false}
                        outlined
                        allowClear
                        placeholder="Инициатор"
                        value={logFilters.runType}
                        options={RUN_TYPE_OPTIONS}
                        isOrganisationSelected={isOrgSelected}
                        handleChange={(runType) => {
                          if (typeof runType === 'string' && isRunType(runType))
                            updateLogFilters({ runType })
                        }}
                        handleClear={() =>
                          updateLogFilters({ runType: undefined })
                        }
                      />
                    </HoverElement>
                  </div>
                  <AtomDateRangePicker
                    value={{
                      from: logFilters.dateFrom ?? null,
                      to: logFilters.dateTo ?? null,
                    }}
                    onChange={({ from, to }) =>
                      updateLogFilters({
                        dateFrom: from,
                        dateTo: from && to?.isBefore(from, 'day') ? null : to,
                      })
                    }
                    isOrganisationSelected={isOrgSelected}
                  />
                  <AtomButton
                    variant="secondary"
                    onClick={() => setLogFilters({})}
                    disabled={!hasActiveLogFilters(logFilters)}
                  >
                    Сбросить
                  </AtomButton>
                </div>
                <AtomVirtualizedTable
                  ariaLabel="Журнал загрузок"
                  data={logs}
                  columns={logColumns}
                  getRowId={(row) => row.id}
                  columnWidths={getColumnWidths(logColumns)}
                  isOrganisationSelected={isOrgSelected}
                  isStale={detailsLoading}
                  onRowClick={setDetailsLog}
                  hasFloatingHeader
                  emptyContent={
                    detailsLoading ? <Loader size={24} /> : undefined
                  }
                  infiniteScroll={{
                    hasNextPage: logsHasMore,
                    isFetchingNextPage: logsLoadingMore,
                    onLoadMore: () => void loadMoreLogs(),
                  }}
                />
              </>
            )}
          </div>
        </>
      )}

      <SupplierCreationModal
        key={`supplier-${supplierModalKey}`}
        open={supplierModalOpen}
        supplier={editingSupplier}
        modal={modal}
        onClose={() => setSupplierModalOpen(false)}
        onSaved={handleSupplierSaved}
        onStoresChanged={handleSupplierStoresChanged}
      />
      <ConfigModal
        key={`config-${configModalKey}`}
        open={configModalOpen}
        supplier={
          selectedSupplier ??
          suppliers.find(
            (supplier) => supplier.id === editingConfig?.productSupplier?.id
          ) ??
          null
        }
        suppliers={suppliers}
        supplierMode={
          isProductSupplierUser ||
          selectedSupplierId === SELF_SUPPLIER_SNAPSHOT_KEY
            ? 'self'
            : 'managed'
        }
        config={editingConfig}
        stores={stores}
        configs={configs}
        storesLoading={detailsLoading || modalDataRefreshing}
        brands={brands}
        brandsLoading={brandsLoading}
        canEditPrices={isStockManager}
        modal={modal}
        onClose={() => {
          setConfigModalOpen(false)
          setEditingConfig(null)
        }}
        onSaved={(savedConfig) => {
          setConfigModalOpen(false)
          setEditingConfig(null)
          if (!selectedSupplierId) return
          applyUpdatedConfig(selectedSupplierId, savedConfig)
        }}
        onPersisted={(persistedConfig) => {
          if (!selectedSupplierId) return
          applyUpdatedConfig(selectedSupplierId, persistedConfig)
        }}
        onRun={runImport}
      />
      <PositionCheckModal
        key={`position-${positionModalKey}`}
        open={!!positionConfig}
        config={positionConfig}
        modal={modal}
        onClose={() => setPositionConfig(null)}
      />
      {detailsLog && (
        <StockImportLogDetailsModal
          log={detailsLog}
          source={getLogSourceInfo(
            detailsLog,
            configsById.get(detailsLog.configId)
          )}
          modal={modal}
          showPrices={
            isStockManager &&
            (configsById.get(detailsLog.configId)?.pricesEnabled === true ||
              detailsLog.priceCancelReason !== undefined)
          }
          onOpenConfig={(configId) => {
            setDetailsLog(null)
            void openConfigModal({ id: configId })
          }}
          onClose={() => setDetailsLog(null)}
        />
      )}
      <MissingSkuModal
        open={!!missingLog}
        log={missingLog}
        modal={modal}
        onClose={() => setMissingLog(null)}
      />
    </div>
  )
}

export default StockImportsTab
